/**
 * 組織代碼 ＝ Store 子網域的第一段（{code}.store.gimmatek.beautinq.com），
 * 所以必須符合 DNS label 規則：小寫英數與 `-`，不得以 `-` 開頭或結尾，長度 2～63。
 */
export const ORG_CODE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])$/

/**
 * 不可作為組織代碼的保留字（CNCY-10757 #6）。
 * 平台子網域（admin／store／client）與常見的基礎設施名稱都擋掉，避免與現有或未來的網域衝突。
 */
export const RESERVED_ORG_CODES = [
  'admin',
  'store',
  'client',
  'api',
  'www',
  'auth',
  'oauth',
  'login',
  'app',
  'static',
  'assets',
  'cdn',
  'image',
  'img',
  'mail',
  'dashboard',
  'dash',
  'status',
  'docs',
  'help',
  'support',
  'test',
  'dev',
  'staging',
  'uat',
  'prod',
] as const

export const ORG_CODE_MESSAGES = {
  format: '組織代碼只能使用小寫英文、數字與「-」，長度 2～63 字，且不可以「-」開頭或結尾',
  reserved: '此代碼為系統保留字，請改用其他代碼',
} as const

export type OrgCodeValidation =
  | { ok: true }
  | { ok: false; reason: keyof typeof ORG_CODE_MESSAGES; message: string }

const reservedCodes = new Set<string>(RESERVED_ORG_CODES)

export function isReservedOrgCode(code: string): boolean {
  return reservedCodes.has(code.toLowerCase())
}

export function validateOrgCode(code: string): OrgCodeValidation {
  if (!ORG_CODE_PATTERN.test(code)) {
    return { ok: false, reason: 'format', message: ORG_CODE_MESSAGES.format }
  }
  if (isReservedOrgCode(code)) {
    return { ok: false, reason: 'reserved', message: ORG_CODE_MESSAGES.reserved }
  }
  return { ok: true }
}

export type HostResolution =
  | { kind: 'entry' }
  | { kind: 'org'; code: string }
  | { kind: 'invalid' }

/**
 * 從 Store 的 host 解析組織代碼。
 *
 * 以 base domain 後綴比對，**不看 host 有幾段**：
 * - `store.gimmatek.beautinq.com`      → entry（未指定組織的入口，不是組織 `store`）
 * - `acme.store.gimmatek.beautinq.com` → org `acme`
 * - 多段前綴、保留字、格式不符、不屬於 base domain → invalid
 */
export function parseOrgCodeFromHost(host: string, baseDomain: string): HostResolution {
  const hostname = normalizeHostname(host)
  const base = normalizeHostname(baseDomain)
  if (!base) return { kind: 'invalid' }
  if (hostname === base) return { kind: 'entry' }

  const suffix = `.${base}`
  if (!hostname.endsWith(suffix)) return { kind: 'invalid' }

  const code = hostname.slice(0, -suffix.length)
  if (code.includes('.') || !validateOrgCode(code).ok) return { kind: 'invalid' }
  return { kind: 'org', code }
}

function normalizeHostname(value: string): string {
  return value.trim().toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '')
}
